import { Scheduler } from "@bryntum/scheduler-thin";
import { DiaryZoomLevels } from "@app/view/diary/diary-viewer/diary-zoom-levels";
import { FilterConfig } from "@bryntum/grid-thin";
import {
  CollectionFilterConfig,
  FieldFilterPickerConfig,
  Store as BryntumStore
} from "@bryntum/core-thin";
import { WorkMode } from "@app/models/rest/resource";

export class DiaryDefaultsOverview {
  private static skillsStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static zonesStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  private static teamsStore = new BryntumStore({
    fields: ["id", "name"],
    data: []
  });

  public static setFilterData(skills: any[], zones: any[], teams?: any[]) {
    // Ensure we have valid arrays and filter out any invalid entries
    const validSkills = (skills || []).filter(
      (skill) => skill && skill.id && skill.description
    );
    const validZones = (zones || []).filter(
      (zone) => zone && zone.id && zone.name
    );
    const validTeams = (teams || []).filter(
      (team) => team && team.id && team.name
    );

    const skillData = validSkills.map((skill) => ({
      id: skill.id,
      name: skill.description || "Unknown Skill"
    }));
    const zoneData = validZones.map((zone) => ({
      id: zone.id,
      name: zone.name || "Unknown Zone"
    }));

    // Clear and add new data to stores
    this.skillsStore.removeAll();
    if (skillData.length > 0) {
      this.skillsStore.add(skillData);
    }

    this.zonesStore.removeAll();
    if (zoneData.length > 0) {
      this.zonesStore.add(zoneData);
    }

    // Handle teams if provided
    if (validTeams.length > 0) {
      const teamData = validTeams.map((team) => ({
        id: team.id,
        name: team.name || "Unknown Team"
      }));
      this.teamsStore.removeAll();
      this.teamsStore.add(teamData);
      console.log("Updated diary overview teams store with:", teamData);
    }

    console.log("Updated diary overview skills store with:", skillData);
    console.log("Updated diary overview zones store with:", zoneData);
  }

  public static getSkillsStore() {
    return this.skillsStore;
  }

  public static getZonesStore() {
    return this.zonesStore;
  }

  public static getTeamsStore() {
    return this.teamsStore;
  }
  public static FILTER_CONFIG: Partial<FilterConfig> = {
    pickerConfig: {
      showAddFilterButton: false,
      dateFormat: "DD/MM/YYYY",

      canDeleteFilter: (filter) => {
        return false;
      },
      getFieldFilterPickerConfig: (
        collectionFilterConfig: CollectionFilterConfig
      ): Partial<FieldFilterPickerConfig> => {
        switch (collectionFilterConfig.property) {
          case "skillText":
          case "zoneText":
          case "team": {
            const fixedFieldResult: any = {
              operatorLocked: true,
              propertyLocked: true,
              // store: this.teamStore,
              operators: {
                string: [
                  {
                    value: "isIncludedIn",
                    text: "Filter by values",
                    argCount: 1
                  }
                ]
              }
            };
            fixedFieldResult.filter = {
              id: `${collectionFilterConfig.property}-isIncludedIn`,
              property: collectionFilterConfig.property,
              operator: "isIncludedIn",
              value: collectionFilterConfig.value,
              disabled: collectionFilterConfig.disabled
            };
            // }
            return fixedFieldResult;
          }
        }
        return {};
      }
    }
  };
  public static DEFAULT_DIARY_SETTINGS: any = {
    barMargin: 5,
    rowHeight: 60,
    columnLines: true,
    // eventLayout: 'none',
    eventAnimations: false,
    enableEventAnimations: false,
    showRemoveEventInContextMenu: true,
    showAddEventInContextMenu: true,
    eventStyle: "bordered",
    multiEventSelect: false,
    // managedEventSizing: false,
    scrollable: true,
    forceFit: true,
    fillTicks: true,
    autoHeight: false,
    zoomLevels: DiaryZoomLevels.ZOOM_LEVELS,
    // minZoomLevel: 1,
    // maxZoomLevel: 1,
    zoomOnMouseWheel: false,
    zoomOnTimeAxisDoubleClick: false,
    viewPreset: "dayAndWeek",
    cls: "sch-container",
    style: "height: calc(100vh - 128px)",
    columns: [
      {
        id: "resource",
        type: "resourceInfo",
        imagePath: "assets/",
        defaultImageName: "profile.png",
        showImage: true,
        text: "Resource",
        width: 200,
        showEventCount: false,
        showRole: false
      },
      {
        id: "team",
        text: "Team",
        width: 100,
        field: "team",
        hidden: true,
        renderer: ({ record }) => {
          if (record.team) {
            return record.team;
          }
          return "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by teams...",
            store: DiaryDefaultsOverview.teamsStore
          },
          // Custom filtering function to handle the team structure
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            // In diary overview, team is stored as a string, so we need to match by name
            if (!record.team) return false;

            // Get team names from the store for the selected IDs
            const selectedTeamNames = value
              .map((teamId) => {
                const teamRecord =
                  DiaryDefaultsOverview.teamsStore.getById(teamId);
                return teamRecord ? teamRecord.get("name") : null;
              })
              .filter((name) => name !== null);

            return selectedTeamNames.includes(record.team);
          }
        }
      },
      {
        id: "skills",
        width: 360,
        text: "Skills",
        field: "skillText",
        hidden: true,
        renderer: ({ record }) => {
          return record.skillText || "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by skills...",
            store: DiaryDefaultsOverview.skillsStore
          },
          // Custom filtering function to handle the skillText field
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            if (!record.skillText) return false;

            // Get skill names from the store for the selected IDs
            const selectedSkillNames = value
              .map((skillId) => {
                const skillRecord =
                  DiaryDefaultsOverview.skillsStore.getById(skillId);
                return skillRecord ? skillRecord.get("name") : null;
              })
              .filter((name) => name !== null);

            console.log(
              "Diary overview skill filter - Selected skill names:",
              selectedSkillNames
            );
            console.log(
              "Diary overview skill filter - Record skillText:",
              record.skillText
            );

            // Check if any of the selected skills are in the skillText string
            const matches = selectedSkillNames.some((skillName) =>
              record.skillText.includes(skillName)
            );

            console.log("Diary overview skill filter - Matches:", matches);
            return matches;
          }
        }
      },
      {
        id: "zones",
        text: "Zones",
        field: "zoneText",
        hidden: true,
        width: 200,
        renderer: ({ record }) => {
          return record.zoneText || "";
        },
        filterable: {
          filterField: {
            type: "combo",
            multiSelect: true,
            valueField: "id",
            displayField: "name",
            placeholder: "Filter by zones...",
            store: DiaryDefaultsOverview.zonesStore
          },
          // Custom filtering function to handle the zoneText field
          filterFn: ({ value, record }) => {
            if (!value || value.length === 0) return true;

            if (!record.zoneText) return false;

            // Get zone names from the store for the selected IDs
            const selectedZoneNames = value
              .map((zoneId) => {
                const zoneRecord =
                  DiaryDefaultsOverview.zonesStore.getById(zoneId);
                return zoneRecord ? zoneRecord.get("name") : null;
              })
              .filter((name) => name !== null);

            // Check if any of the selected zones are in the zoneText string
            return selectedZoneNames.some((zoneName) =>
              record.zoneText.includes(zoneName)
            );
          }
        }
      },
      {
        id: "workMode",
        text: "Mode",
        field: "workMode",
        hidden: true,
        width: 50,
        renderer: ({ record }) =>
          record.workMode && record.workMode === WorkMode.Offline
            ? "Offline"
            : "Online"
      }
    ],
    features: null // ,
    // resourceTimeRanges: true
  };
  public static DEFAULT_DIARY_FEATURES: any = {
    eventDrag: false,
    eventDragCreate: false,
    scheduleContextMenu: {
      items: {
        addEvent: false
      }
    },
    eventContextMenu: {
      items: {
        deleteEvent: false,
        unassignEvent: false
      }
    },
    eventEdit: {
      showNameField: false
    },
    columnResize: false,
    regionResize: false,
    eventFilter: true,
    eventResize: false,
    // group: 'team',
    scheduleTooltip: true,
    timeRanges: { showHeaderElements: true, showCurrentTimeLine: true },
    resourceTimeRanges: false,
    cellEdit: false,
    filter: DiaryDefaultsOverview.FILTER_CONFIG,
    headerContextMenu: false,
    timeAxisHeaderMenu: {
      zoomLevel: false,
      dateRange: false
    }
  };

  public static Create(config, features): any {
    let mergedConfig: any = DiaryDefaultsOverview.DEFAULT_DIARY_SETTINGS;
    mergedConfig = Object.assign(mergedConfig, config);
    mergedConfig.features = DiaryDefaultsOverview.DEFAULT_DIARY_FEATURES;
    mergedConfig.features = Object.assign(mergedConfig.features, features);

    return new Scheduler(mergedConfig);
  }
}
